import type { MhAvatar } from "@uzverse/contracts";
import { hashOf } from "../hash.js";
import { MH_KIT } from "./kit.gen.js";

/**
 * A MakeHuman character as data (`avatar.mh` in the settings): sex, slider values in -1..1 and
 * the kit items it wears. Everything here is pure and synchronous (the kit's ids and slots come
 * from the generated `kit.gen.ts`), so a spec can be validated, defaulted, migrated and encoded
 * without downloading a single model.
 */
export type MhSpec = MhAvatar;
export type Sex = MhSpec["sex"];
export type Slot = (typeof MH_KIT.items)[number]["slot"];
export type SliderGroup = (typeof MH_KIT.sliders)[number]["group"];

export interface KitItem {
  id: string;
  slot: Slot;
  style?: string;
  sexes: readonly Sex[];
  hidesHair?: boolean;
}
export const ITEMS: readonly KitItem[] = MH_KIT.items;
export const SLIDERS: readonly { id: string; group: SliderGroup }[] = MH_KIT.sliders;
export const SLIDER_IDS: readonly string[] = SLIDERS.map((s) => s.id);
export const EYES: readonly string[] = MH_KIT.eyes;
const BY_ID = new Map(ITEMS.map((i) => [i.id, i]));

export const itemById = (id: string | null | undefined): KitItem | undefined =>
  id ? BY_ID.get(id) : undefined;

/** Items of a slot, the ones made for `sex` first (the others still fit, they are just less usual). */
export function itemsFor(slot: Slot, sex?: Sex): KitItem[] {
  const all = ITEMS.filter((i) => i.slot === slot);
  if (!sex) return all;
  return [
    ...all.filter((i) => i.sexes.includes(sex)),
    ...all.filter((i) => !i.sexes.includes(sex)),
  ];
}

/** Skin tones of a sex, light to deep, as indices into the kit's skin list. */
export function skinsFor(sex: Sex): number[] {
  return MH_KIT.skins.flatMap((s, i) => (s.sex === sex ? [i] : []));
}
export const SKIN_TONE_COUNT = 8;
/** The kit skin of a spec (its `skin` is the tone 0..7 within the sex). */
export function skinIndex(spec: Pick<MhSpec, "sex" | "skin">): number {
  const list = skinsFor(spec.sex);
  return list[Math.max(0, Math.min(list.length - 1, spec.skin))]!;
}

export const HAIR_COLORS = [
  "#1c1714",
  "#3b2a20",
  "#5a3d2b",
  "#7d5a3c",
  "#a07c50",
  "#c8a56a",
  "#8a3f22",
  "#a34a2a",
  "#8e8c88",
  "#d8d4cc",
] as const;
export const CLOTH_COLORS = [
  "#1f2a44",
  "#34363b",
  "#18191c",
  "#6b6e74",
  "#b9a588",
  "#5b4636",
  "#e8e6e1",
  "#a8323a",
  "#3f6b48",
  "#3b5f8f",
  "#c69a32",
  "#d48aa0",
  "#2c7a78",
  "#8f7fb8",
  "#6d2433",
  "#f2efe6",
] as const;
/** Skin tone swatches for the UI (the textures carry the real color). */
export const SKIN_SWATCHES = [
  "#f3d9c6",
  "#e9c4a8",
  "#dcb08e",
  "#c99b74",
  "#b07a52",
  "#8d5a3b",
  "#6a4129",
  "#4a2d1e",
] as const;
export const EYE_SWATCHES: Record<string, string> = {
  brown: "#5a3a22",
  hazel: "#8a6a3a",
  green: "#5f7d4a",
  blue: "#4f7fb3",
  grey: "#8a949c",
  teal: "#3f8a86",
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));
const round3 = (v: number) => Math.round(v * 1000) / 1000;

function pickItem(id: unknown, slot: Slot, sex: Sex, fallback: string | null): string | null {
  const it = typeof id === "string" ? BY_ID.get(id) : undefined;
  if (it && it.slot === slot) return it.id;
  return fallback;
}
const firstOf = (slot: Slot, sex: Sex): string => itemsFor(slot, sex)[0]!.id;

/** The accessory group of an item (one pair of glasses and one hat at most). */
export const accessoryKind = (id: string): string => id.split("-")[0]!;

/**
 * Keeps every value in range and every id one the kit has for its slot; anything unknown falls back
 * to the slot's first item for that sex. Top and bottom are never empty. Zero morphs are dropped.
 */
export function normalizeMh(raw: Partial<MhSpec> | null | undefined): MhSpec {
  const r = raw ?? {};
  const sex: Sex = r.sex === "female" ? "female" : "male";
  const morphs: Record<string, number> = {};
  const known = new Set(SLIDER_IDS);
  for (const [k, v] of Object.entries(r.morphs ?? {})) {
    if (!known.has(k) || typeof v !== "number" || !Number.isFinite(v)) continue;
    const c = round3(clamp1(v));
    if (c !== 0) morphs[k] = c;
  }
  const o = (r.outfit ?? {}) as Partial<MhSpec["outfit"]>;
  const colors = (o.colors ?? {}) as Partial<MhSpec["outfit"]["colors"]>;
  const color = (c: unknown, d: string) =>
    typeof c === "string" && HEX.test(c) ? c.toLowerCase() : d;
  const accessories: string[] = [];
  const kinds = new Set<string>();
  for (const a of Array.isArray(r.accessories) ? r.accessories : []) {
    const it = typeof a === "string" ? BY_ID.get(a) : undefined;
    if (!it || it.slot !== "accessory" || kinds.has(accessoryKind(it.id))) continue;
    kinds.add(accessoryKind(it.id));
    accessories.push(it.id);
    if (accessories.length >= 2) break;
  }
  const intIn = (v: unknown, n: number, d: number) =>
    typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.min(n - 1, Math.trunc(v))) : d;
  return {
    v: 1,
    sex,
    morphs,
    skin: intIn(r.skin, SKIN_TONE_COUNT, 1),
    eyes: intIn(r.eyes, EYES.length, 0),
    brows: pickItem(r.brows, "brows", sex, firstOf("brows", sex))!,
    hair: r.hair === null ? null : pickItem(r.hair, "hair", sex, firstOf("hair", sex)),
    hairColor: color(r.hairColor, HAIR_COLORS[1]),
    beard: sex === "female" ? null : (pickItem(r.beard, "beard", sex, null) ?? null),
    outfit: {
      top: pickItem(o.top, "top", sex, firstOf("top", sex))!,
      bottom: pickItem(o.bottom, "bottom", sex, firstOf("bottom", sex))!,
      shoes: o.shoes === null ? null : pickItem(o.shoes, "shoes", sex, firstOf("shoes", sex)),
      colors: {
        top: color(colors.top, CLOTH_COLORS[0]),
        bottom: color(colors.bottom, CLOTH_COLORS[1]),
        shoes: color(colors.shoes, CLOTH_COLORS[2]),
      },
    },
    accessories,
  };
}

/** The ids a spec wears, in draw order (body first is implied). */
export function wornItems(spec: MhSpec): string[] {
  const lashes = spec.sex === "female" ? "lashes-f" : "lashes-m";
  const hat = spec.accessories.some((a) => itemById(a)?.hidesHair);
  return [
    "eyes",
    lashes,
    spec.brows,
    ...(spec.hair && !hat ? [spec.hair] : []),
    ...(spec.beard ? [spec.beard] : []),
    spec.outfit.top,
    spec.outfit.bottom,
    ...(spec.outfit.shoes ? [spec.outfit.shoes] : []),
    ...spec.accessories,
  ].filter((id) => BY_ID.has(id));
}

// ---------------------------------------------------------------- presets

export interface MhPreset {
  id: string;
  spec: MhSpec;
}

type P = Omit<Partial<MhSpec>, "outfit"> & {
  outfit: [string, string, string | null, string, string, string];
};
const mk = (sex: Sex, id: string, p: P): MhPreset => ({
  id,
  spec: normalizeMh({
    ...p,
    sex,
    outfit: {
      top: p.outfit[0],
      bottom: p.outfit[1],
      shoes: p.outfit[2],
      colors: { top: p.outfit[3], bottom: p.outfit[4], shoes: p.outfit[5] },
    },
  }),
});

/** Eight good-looking starting points per sex (the creator's "Hazır" row, the default looks). */
export const PRESETS: Record<Sex, readonly MhPreset[]> = {
  male: [
    mk("male", "executive", {
      skin: 1,
      eyes: 0,
      brows: "brows-natural",
      hair: "hair-short-side",
      hairColor: "#3b2a20",
      morphs: {
        height: 0.25,
        jawWidth: 0.3,
        chinProtrusion: 0.2,
        shoulders: 0.2,
        noseBridge: 0.15,
      },
      outfit: ["top-suit-tie", "bottom-suit", "shoes-oxford", "#1f2a44", "#1f2a44", "#18191c"],
    }),
    mk("male", "creative", {
      skin: 3,
      eyes: 1,
      brows: "brows-bold",
      hair: "hair-short-messy",
      hairColor: "#1c1714",
      beard: "beard-stubble",
      accessories: ["glasses-frames"],
      morphs: { weight: -0.15, faceTaper: 0.2, noseLength: 0.2, cheekbones: 0.3 },
      outfit: [
        "top-jacket-casual",
        "bottom-jeans",
        "shoes-sneaker-white",
        "#5b4636",
        "#3b5f8f",
        "#e8e6e1",
      ],
    }),
    mk("male", "athlete", {
      skin: 4,
      eyes: 0,
      brows: "brows-straight",
      hair: "hair-short-crop",
      hairColor: "#1c1714",
      morphs: {
        muscle: 0.55,
        weight: -0.2,
        shoulders: 0.45,
        height: 0.35,
        neckWidth: 0.3,
        jawWidth: 0.25,
      },
      outfit: ["top-polo", "bottom-shorts", "shoes-sneaker-blue", "#2c7a78", "#34363b", "#3b5f8f"],
    }),
    mk("male", "scholar", {
      skin: 2,
      eyes: 3,
      brows: "brows-soft",
      hair: "hair-short-side",
      hairColor: "#7d5a3c",
      beard: "beard-full",
      accessories: ["glasses-round"],
      morphs: { age: 0.35, faceFullness: 0.15, weight: 0.1, noseSize: 0.15 },
      outfit: ["top-sweater", "bottom-chinos", "shoes-loafer", "#6d2433", "#b9a588", "#5b4636"],
    }),
    mk("male", "casual", {
      skin: 6,
      eyes: 0,
      brows: "brows-natural",
      hair: "hair-afro",
      hairColor: "#1c1714",
      morphs: { lipFullness: 0.35, noseWidth: 0.3, height: 0.15, faceSquare: 0.15 },
      outfit: ["top-tee", "bottom-jeans", "shoes-sneaker-white", "#e8e6e1", "#3b5f8f", "#e8e6e1"],
    }),
    mk("male", "mature", {
      skin: 1,
      eyes: 4,
      brows: "brows-bold",
      hair: "hair-short-side",
      hairColor: "#8e8c88",
      beard: "beard-moustache",
      morphs: { age: 0.75, weight: 0.25, belly: 0.25, faceFullness: 0.2, height: -0.1 },
      outfit: [
        "top-suit-classic",
        "bottom-suit-classic",
        "shoes-oxford",
        "#34363b",
        "#34363b",
        "#18191c",
      ],
    }),
    mk("male", "young", {
      skin: 5,
      eyes: 1,
      brows: "brows-thin",
      hair: "hair-pixie",
      hairColor: "#3b2a20",
      morphs: { age: -0.7, weight: -0.2, eyeSize: 0.2, headSize: 0.1 },
      outfit: ["top-tee", "bottom-cargo", "shoes-sneaker-blue", "#a8323a", "#5b4636", "#3b5f8f"],
    }),
    mk("male", "bearded", {
      skin: 2,
      eyes: 2,
      brows: "brows-bold",
      hair: "hair-long",
      hairColor: "#5a3d2b",
      beard: "beard-long",
      morphs: { height: 0.2, muscle: 0.2, browHeight: -0.2 },
      outfit: [
        "top-shirt-striped",
        "bottom-chinos",
        "shoes-boots",
        "#e8e6e1",
        "#34363b",
        "#5b4636",
      ],
    }),
  ],
  female: [
    mk("female", "executive", {
      skin: 1,
      eyes: 3,
      brows: "brows-arched",
      hair: "hair-bob",
      hairColor: "#3b2a20",
      morphs: { height: 0.2, cheekbones: 0.3, lipFullness: 0.2, noseWidth: -0.2, eyeTilt: 0.15 },
      outfit: ["top-suit-f", "bottom-suit-f", "shoes-booties", "#1f2a44", "#1f2a44", "#18191c"],
    }),
    mk("female", "creative", {
      skin: 3,
      eyes: 2,
      brows: "brows-thin",
      hair: "hair-pixie",
      hairColor: "#1c1714",
      accessories: ["glasses-round"],
      morphs: { weight: -0.1, eyeSize: 0.2, faceTaper: 0.2, lipFullness: 0.3 },
      outfit: ["top-turtleneck", "bottom-jeans-f", "shoes-flats", "#18191c", "#3b5f8f", "#18191c"],
    }),
    mk("female", "athlete", {
      skin: 5,
      eyes: 0,
      brows: "brows-straight",
      hair: "hair-ponytail",
      hairColor: "#1c1714",
      morphs: { muscle: 0.35, weight: -0.2, height: 0.3, shoulders: 0.15, legLength: 0.2 },
      outfit: [
        "top-sport-f",
        "bottom-leggings",
        "shoes-sneaker-white",
        "#2c7a78",
        "#18191c",
        "#e8e6e1",
      ],
    }),
    mk("female", "scholar", {
      skin: 2,
      eyes: 4,
      brows: "brows-soft",
      hair: "hair-long",
      hairColor: "#7d5a3c",
      accessories: ["glasses-frames"],
      morphs: { faceFullness: 0.1, noseLength: 0.15, mouthWidth: -0.15, age: 0.15 },
      outfit: ["top-blouse", "bottom-skirt", "shoes-flats", "#e8e6e1", "#34363b", "#5b4636"],
    }),
    mk("female", "casual", {
      skin: 6,
      eyes: 0,
      brows: "brows-natural",
      hair: "hair-afro",
      hairColor: "#1c1714",
      morphs: { lipFullness: 0.4, noseWidth: 0.25, hips: 0.25, buttocks: 0.2 },
      outfit: [
        "top-tee-f",
        "bottom-jeans-f",
        "shoes-sneaker-white",
        "#c69a32",
        "#3b5f8f",
        "#e8e6e1",
      ],
    }),
    mk("female", "mature", {
      skin: 0,
      eyes: 3,
      brows: "brows-arched",
      hair: "hair-bob-long",
      hairColor: "#d8d4cc",
      morphs: { age: 0.7, weight: 0.2, faceFullness: 0.15 },
      outfit: [
        "top-suit-f-double",
        "bottom-suit-f2",
        "shoes-booties",
        "#6b6e74",
        "#34363b",
        "#18191c",
      ],
    }),
    mk("female", "young", {
      skin: 4,
      eyes: 1,
      brows: "brows-thin",
      hair: "hair-braid",
      hairColor: "#a07c50",
      morphs: { age: -0.6, weight: -0.15, eyeSize: 0.25, noseSize: -0.2 },
      outfit: ["top-tee-f", "bottom-shorts", "shoes-sneaker-blue", "#d48aa0", "#3b5f8f", "#3b5f8f"],
    }),
    mk("female", "elegant", {
      skin: 7,
      eyes: 0,
      brows: "brows-arched",
      hair: "hair-ponytail",
      hairColor: "#1c1714",
      morphs: {
        height: 0.25,
        cheekbones: 0.35,
        neckLength: 0.3,
        lipFullness: 0.25,
        jawWidth: -0.2,
      },
      outfit: ["top-suit-f2", "bottom-suit-f2", "shoes-booties", "#6d2433", "#6d2433", "#18191c"],
    }),
  ],
};

// ---------------------------------------------------------------- defaults and randomising

/** A small deterministic PRNG (mulberry32) so a seed always gives the same character. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Common first names whose sex is clear (tr and en); anything else is decided by the hash.
const FEMALE_NAMES =
  /^(ay[sş]e|fatma|zeynep|elif|merve|esra|b[uü]şra|emine|hatice|sel[iı]n|derya|ebru|gamze|g[uü]l.*|nur.*|ece|defne|ipek|irem|melis|deniz e|sevgi|yasemin|aylin|asl[iı]|t[uü]lay|özge|ozge|sibel|hande|seda|pınar|pinar|sinem|tuğba|tugba|mary|anna|emma|olivia|sophia|sarah|laura|julia|maria|lisa|emily|jessica|kate|anne|linda|susan|elena|nina|eva)$/i;
const MALE_NAMES =
  /^(ahmet|mehmet|mustafa|ali|h[uü]seyin|hasan|ibrahim|[iı]smail|osman|yusuf|murat|emre|burak|can|cem|kerem|mert|onur|serkan|kemal|ömer|omer|erdem|eren|arda|baran|ege|efe|berk|selim|tolga|volkan|umut|okan|oğuz|oguz|john|james|michael|david|robert|william|thomas|daniel|paul|mark|peter|alex|chris|tom|jack|adam|leo|max|ben)$/i;

/** The sex a display name most likely belongs to, or null when the name does not say. */
export function sexFromName(name: string): Sex | null {
  const first = name.trim().split(/\s+/)[0] ?? "";
  if (FEMALE_NAMES.test(first)) return "female";
  if (MALE_NAMES.test(first)) return "male";
  return null;
}

/**
 * The character of someone who has not made one: a preset picked by the hash of the name (or the
 * id), with a light, seeded variation of the face so two people rarely look the same. Always a
 * realistic, average build.
 */
export function defaultMh(seed: string, name = ""): MhSpec {
  const h = hashOf(`mh:${name.trim().toLowerCase() || seed}`);
  const r = rng(h);
  const sex = sexFromName(name) ?? (r() < 0.5 ? "male" : "female");
  const list = PRESETS[sex];
  const base = list[Math.floor(r() * list.length)]!.spec;
  const morphs: Record<string, number> = { ...base.morphs };
  for (const id of [
    "noseWidth",
    "noseLength",
    "eyeSpacing",
    "lipFullness",
    "jawWidth",
    "cheekbones",
    "height",
  ]) {
    morphs[id] = round3(clamp1((morphs[id] ?? 0) + (r() - 0.5) * 0.4));
  }
  return normalizeMh({
    ...base,
    morphs,
    skin: Math.floor(r() * SKIN_TONE_COUNT),
    eyes: Math.floor(r() * EYES.length),
    hairColor: HAIR_COLORS[Math.floor(r() * 6)]!,
  });
}

/** Rolls new values: every slider of `groups` within ±`amount`, items and colors too when asked. */
export function randomizeMh(
  spec: MhSpec,
  seed: number,
  opts: { groups?: readonly SliderGroup[]; amount?: number; looks?: boolean } = {},
): MhSpec {
  const r = rng(seed);
  const amount = opts.amount ?? 0.6;
  const groups = opts.groups ?? ["body", "face"];
  const morphs: Record<string, number> = { ...spec.morphs };
  for (const s of SLIDERS) {
    if (!groups.includes(s.group)) continue;
    // A bell-ish curve: most values near the middle, a few strong ones.
    const v = (r() + r() + r() - 1.5) / 1.5;
    morphs[s.id] = round3(clamp1(v * amount));
  }
  if (!opts.looks) return normalizeMh({ ...spec, morphs });
  const pick = <T>(xs: readonly T[]) => xs[Math.floor(r() * xs.length)]!;
  const own = (slot: Slot) => itemsFor(slot, spec.sex).filter((i) => i.sexes.includes(spec.sex));
  return normalizeMh({
    ...spec,
    morphs,
    skin: Math.floor(r() * SKIN_TONE_COUNT),
    eyes: Math.floor(r() * EYES.length),
    brows: pick(own("brows")).id,
    hair: pick(own("hair")).id,
    hairColor: pick(HAIR_COLORS),
    beard: spec.sex === "male" && r() < 0.5 ? pick(own("beard")).id : null,
    outfit: {
      top: pick(own("top")).id,
      bottom: pick(own("bottom")).id,
      shoes: pick(own("shoes")).id,
      colors: {
        top: pick(CLOTH_COLORS),
        bottom: pick(CLOTH_COLORS.slice(0, 6)),
        shoes: pick(CLOTH_COLORS.slice(0, 6)),
      },
    },
    accessories: r() < 0.25 ? [pick(itemsFor("accessory")).id] : [],
  });
}

// ---------------------------------------------------------------- migration from the palette specs

/** Legacy palette fields (`AvatarSpec` before MakeHuman). */
export interface LegacyLook {
  body: "male" | "female";
  skin: number;
  hair: number;
  hairColor: number;
  outfit: number;
  outfitColor: number;
}
const LEGACY_HAIR_COLORS = [
  "#1b1512",
  "#3a2618",
  "#5b3a22",
  "#7b4a26",
  "#a0522d",
  "#c9a15a",
  "#9a9a9a",
  "#b8472a",
];
const LEGACY_OUTFIT_COLORS = [
  "#2f4a8a",
  "#e9e6df",
  "#b4343f",
  "#2a8c82",
  "#d9a22a",
  "#6f4fb0",
  "#4c8a45",
  "#24262c",
  "#7d838c",
  "#d9708f",
];

/**
 * A palette character (the Quaternius or office era) as the nearest MakeHuman preset: same sex,
 * same place on the skin scale, the hair and outfit color kept, the outfit's kind mapped
 * (tee/shorts -> casual, shirt -> smart, blazer/hoodie -> business).
 */
export function migrateLegacy(old: LegacyLook): MhSpec {
  const sex: Sex = old.body === "female" ? "female" : "male";
  const kind = ["casual", "smart", "business", "sport", "smart"][old.outfit] ?? "casual";
  const presetId =
    sex === "male"
      ? { casual: "casual", smart: "scholar", business: "executive", sport: "athlete" }[kind]!
      : { casual: "casual", smart: "scholar", business: "executive", sport: "athlete" }[kind]!;
  const base = PRESETS[sex].find((p) => p.id === presetId)!.spec;
  const hairColor = LEGACY_HAIR_COLORS[old.hairColor] ?? base.hairColor;
  const top = LEGACY_OUTFIT_COLORS[old.outfitColor] ?? base.outfit.colors.top;
  return normalizeMh({
    ...base,
    skin: Math.max(0, Math.min(SKIN_TONE_COUNT - 1, old.skin)),
    hairColor,
    beard: sex === "male" && old.hair === 5 ? "beard-full" : base.beard,
    outfit: { ...base.outfit, colors: { ...base.outfit.colors, top } },
  });
}

// ---------------------------------------------------------------- compact form (assistant `appearance`)

const NONE = 255;
const nearest = (hex: string, palette: readonly string[]): number => {
  const c = parseInt(hex.slice(1), 16);
  let best = 0;
  let bestD = Infinity;
  palette.forEach((p, i) => {
    const q = parseInt(p.slice(1), 16);
    const d =
      (((c >> 16) & 255) - ((q >> 16) & 255)) ** 2 +
      (((c >> 8) & 255) - ((q >> 8) & 255)) ** 2 +
      ((c & 255) - (q & 255)) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
};
const itemIndex = (id: string | null): number => {
  if (!id) return NONE;
  const i = ITEMS.findIndex((x) => x.id === id);
  return i < 0 ? NONE : i;
};
const itemAt = (i: number | undefined): string | null =>
  i === undefined || i === NONE ? null : (ITEMS[i]?.id ?? null);

export const APPEARANCE_PREFIX = "m1:";

/**
 * A spec in at most 60 characters (`m1:` + base64url of 15 bytes and one nibble per slider), for the
 * assistant's `appearance` setting. Colors snap to the palettes and sliders to 1/7 steps.
 */
export function encodeMh(spec: MhSpec): string {
  const s = normalizeMh(spec);
  const head = [
    (s.sex === "female" ? 0x80 : 0) | 1,
    s.skin,
    s.eyes,
    itemIndex(s.brows),
    itemIndex(s.hair),
    nearest(s.hairColor, HAIR_COLORS),
    itemIndex(s.beard),
    itemIndex(s.outfit.top),
    itemIndex(s.outfit.bottom),
    itemIndex(s.outfit.shoes),
    nearest(s.outfit.colors.top, CLOTH_COLORS),
    nearest(s.outfit.colors.bottom, CLOTH_COLORS),
    nearest(s.outfit.colors.shoes, CLOTH_COLORS),
    itemIndex(s.accessories[0] ?? null),
    itemIndex(s.accessories[1] ?? null),
  ];
  const nib = SLIDER_IDS.map((id) => Math.round((s.morphs[id] ?? 0) * 7) + 8);
  const bytes = new Uint8Array(head.length + Math.ceil(nib.length / 2));
  head.forEach((b, i) => (bytes[i] = b));
  nib.forEach((n, i) => {
    const at = head.length + (i >> 1);
    bytes[at] = bytes[at]! | (i & 1 ? n : n << 4);
  });
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return APPEARANCE_PREFIX + btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Reads `encodeMh`'s form; null for anything else. */
export function decodeMh(text: string | undefined): MhSpec | null {
  if (!text?.startsWith(APPEARANCE_PREFIX)) return null;
  let bin: string;
  try {
    bin = atob(text.slice(APPEARANCE_PREFIX.length).replace(/-/g, "+").replace(/_/g, "/"));
  } catch {
    return null;
  }
  const b = Array.from(bin, (c) => c.charCodeAt(0));
  if (b.length < 15 || (b[0]! & 0x7f) !== 1) return null;
  const morphs: Record<string, number> = {};
  SLIDER_IDS.forEach((id, i) => {
    const byte = b[15 + (i >> 1)];
    if (byte === undefined) return;
    const n = i & 1 ? byte & 15 : byte >> 4;
    if (n !== 8) morphs[id] = (n - 8) / 7;
  });
  return normalizeMh({
    sex: b[0]! & 0x80 ? "female" : "male",
    skin: b[1],
    eyes: b[2],
    brows: itemAt(b[3]) ?? undefined,
    hair: itemAt(b[4]),
    hairColor: HAIR_COLORS[b[5]!] ?? HAIR_COLORS[1],
    beard: itemAt(b[6]),
    outfit: {
      top: itemAt(b[7]) ?? "",
      bottom: itemAt(b[8]) ?? "",
      shoes: itemAt(b[9]),
      colors: {
        top: CLOTH_COLORS[b[10]!] ?? CLOTH_COLORS[0],
        bottom: CLOTH_COLORS[b[11]!] ?? CLOTH_COLORS[1],
        shoes: CLOTH_COLORS[b[12]!] ?? CLOTH_COLORS[2],
      },
    },
    accessories: [itemAt(b[13]), itemAt(b[14])].filter((x): x is string => !!x),
    morphs,
  });
}
